import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { testimonials } from '@/mocks/comics';

const siteUrl = 'http://iitzgd.cn/';

export default function AboutPage() {
  const pageUrl = `${siteUrl}/about`;

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '韩漫大全',
      url: siteUrl,
      description: '韩漫大全是一个专注于韩国漫画的中文在线阅读平台，提供无删减韩漫资源，支持下拉式阅读模式。',
      foundingDate: '2024',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'hello@iitzgd.cn',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'CN',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于我们 - 韩漫大全',
      url: pageUrl,
      description: '了解韩漫大全的品牌故事、团队使命和联系方式。我们致力于为中文读者提供最优质的韩国漫画阅读体验。',
      isPartOf: {
        '@type': 'WebSite',
        name: '韩漫大全',
        url: siteUrl,
      },
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: pageUrl },
      ],
    },
  ];

  const contacts = [
    { icon: 'ri-mail-line', label: '电子邮箱', value: 'hello@iitzgd.cn' },
    { icon: 'ri-wechat-line', label: '微信公众号', value: '韩漫大全' },
    { icon: 'ri-notification-3-line', label: '站内动态', value: '韩漫大全更新' },
    { icon: 'ri-fire-line', label: '热门内容', value: '韩漫大全精选' },
  ];

  const platformStats = [
    { value: '20,000', suffix: '+', label: '漫画总量' },
    { value: '500', suffix: '万', label: '月活用户' },
    { value: '200', suffix: '+', label: '日更新话数' },
    { value: '98', suffix: '%', label: '用户好评率' },
  ];

  // --- business form state --- //
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleBusinessSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) { setFormSubmitted(true); return; }
    setFormSubmitted(true);
    setFormError('');
    form.reset();
  };

  return (
    <>
      <SEOHead
        title="关于我们_韩漫大全_无删减韩国漫画在线阅读平台"
        description="韩漫大全是一个专注于韩国漫画的中文在线阅读平台，提供无删减韩漫资源，支持下拉式阅读模式。了解我们的品牌故事、团队使命和联系方式。"
        keywords="韩漫大全,关于我们,韩国漫画平台,免费漫画,联系方式"
        canonicalUrl={pageUrl}
        schemaData={schema}
      />
      <Navbar />
      <main>
        <div className="pt-16">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          {/* --- brand story --- */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="mx-auto px-4 md:px-6 max-w-5xl">
              <div className="text-center mb-10">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">关于韩漫大全</h1>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  致力于为全球中文读者提供最新、最全、最优质的韩国漫画阅读体验
                </p>
              </div>

              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 mb-12">
                <div className="flex-1">
                  <h2 className="font-heading text-xl text-foreground-900 mb-4">品牌故事</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    韩漫大全成立于2024年，由一群热爱韩国漫画的资深漫迷共同创建。我们深知寻找高质量韩漫资源的不易，因此立志打造一个中文用户友好、内容全面、阅读体验流畅的韩漫平台。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    我们与多家韩国漫画平台和出版社保持密切合作，确保所有上架作品均为正版授权内容。平台支持手机、平板和电脑多终端阅读，独创的下拉式阅读模式让您可以无缝连续浏览，无需频繁翻页，沉浸式体验故事情节。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    我们相信漫画是一种跨越语言和文化的艺术形式。通过高质量的中文翻译和精心的内容编排，我们希望让更多中文读者感受到韩国漫画的魅力。
                  </p>
                </div>

                <div className="flex-1 relative h-56 md:h-72 rounded-lg overflow-hidden bg-gradient-to-br from-secondary-100/60 via-background-50 to-accent-100/40">
                  <div className="absolute top-6 right-10 w-20 h-20 rounded-2xl bg-accent-200/30 rotate-12"></div>
                  <div className="absolute bottom-10 left-6 w-16 h-16 rounded-full bg-secondary-200/30"></div>
                  <div className="absolute top-14 left-14 w-14 h-14 rounded-lg bg-primary-200/30 -rotate-6"></div>
                  <div className="absolute bottom-6 right-14 w-24 h-24 rounded-3xl bg-accent-100/20 rotate-45"></div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-12">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                    <i className="ri-eye-line text-secondary-600 text-lg"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的愿景</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">成为中文世界最受欢迎的韩国漫画阅读平台，让每一位漫迷都能轻松找到心仪的作品。</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                    <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的承诺</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">所有内容均为正版授权，无删减完整呈现，确保读者获得原汁原味的阅读体验。</p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                    <i className="ri-rocket-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">我们的速度</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">与韩国同步更新，每日上架最新话，让您第一时间阅读到最热门的韩漫内容。</p>
                </div>
              </div>
            </div>
          </section>

          {/* --- platform stats --- */}
          <section className="py-10 md:py-14 bg-foreground-900">
            <div className="mx-auto px-4 md:px-6 max-w-5xl">
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-3">平台数据</h2>
                <p className="text-sm text-background-400">用数据说话，我们用心做好每一个细节</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {platformStats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 rounded-lg bg-background-50/5 border border-background-50/10">
                    <div className="text-3xl md:text-4xl font-bold text-accent-400 mb-1">
                      {stat.value}<span className="text-xl">{stat.suffix}</span>
                    </div>
                    <p className="text-sm text-background-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* --- testimonials --- */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="mx-auto px-4 md:px-6 max-w-5xl">
              <h2 className="font-heading text-2xl text-foreground-900 text-center mb-8">用户口碑</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {testimonials.map((t, idx) => (
                  <div key={idx} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">{t.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* --- contact --- */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="mx-auto px-4 md:px-6 max-w-5xl">
              <h2 className="font-heading text-2xl text-foreground-900 text-center mb-8">联系我们</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-12">
                {contacts.map((c) => (
                  <div key={c.label} className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto rounded-full bg-secondary-100 flex items-center justify-center mb-3">
                      <i className={`${c.icon} text-secondary-600`}></i>
                    </div>
                    <h4 className="text-xs text-foreground-500 mb-1">{c.label}</h4>
                    <p className="text-sm text-foreground-800 font-medium whitespace-nowrap">{c.value}</p>
                  </div>
                ))}
              </div>

              {/* --- business cooperation form --- */}
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl text-foreground-900 mb-2">商务合作</h3>
                  <p className="text-sm text-foreground-500">
                    如果您有版权合作、广告投放、内容授权等商务需求，请填写下表，我们会在1-3个工作日内与您联系。
                  </p>
                </div>

                {formSubmitted ? (
                  <div className="text-center py-10 px-6 rounded-lg bg-accent-100/80 border border-accent-200">
                    <div className="w-12 h-12 mx-auto rounded-full bg-accent-200 flex items-center justify-center mb-3">
                      <i className="ri-check-line text-accent-600 text-xl"></i>
                    </div>
                    <p className="font-heading text-lg text-accent-800 mb-1">提交成功！</p>
                    <p className="text-sm text-accent-700">感谢您的合作意向，我们的商务团队会尽快与您取得联系。</p>
                  </div>
                ) : (
                  <form onSubmit={handleBusinessSubmit} className="space-y-4">
                    <input name="phone_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="biz-name" className="block text-xs text-foreground-600 mb-1.5">姓名 *</label>
                        <input
                          id="biz-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="biz-company" className="block text-xs text-foreground-600 mb-1.5">公司名称 *</label>
                        <input
                          id="biz-company"
                          name="company"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="贵公司名称"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="biz-email" className="block text-xs text-foreground-600 mb-1.5">电子邮箱 *</label>
                        <input
                          id="biz-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="hello@iitzgd.cn"
                        />
                      </div>
                      <div>
                        <label htmlFor="biz-phone" className="block text-xs text-foreground-600 mb-1.5">联系电话</label>
                        <input
                          id="biz-phone"
                          name="phone"
                          type="text"
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="您的联系电话"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="biz-type" className="block text-xs text-foreground-600 mb-1.5">合作类型 *</label>
                      <select
                        id="biz-type"
                        name="cooperation_type"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      >
                        <option value="">请选择合作类型</option>
                        <option value="copyright">版权合作</option>
                        <option value="advertising">广告投放</option>
                        <option value="content">内容授权</option>
                        <option value="partnership">战略合作</option>
                        <option value="other">其他</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="biz-message" className="block text-xs text-foreground-600 mb-1.5">合作说明 *</label>
                      <textarea
                        id="biz-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请简要描述您的合作意向和需求（500字以内）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">限500字</p>
                    </div>

                    {formError && (
                      <p className="text-sm text-red-500 bg-red-50 rounded-lg px-4 py-2">{formError}</p>
                    )}

                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                    >
                      提交合作意向
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
